import { useNavigate } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import { getCategories, deleteCategory, getEntries, deleteEntry } from "./.request";
import { useAuth } from "../context/AuthContext";
import { IMAGE_URL } from "../constants/Consts";
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import CreateCategoryModal from './CreateCategoryModal';

const Munasibaat = () => {
  const navigate = useNavigate();
  const { authToken, themeMode } = useAuth();
  const isDark = themeMode === 'dark';
  const IMAGE_SHOW = `${IMAGE_URL}munasibaatImages`;

  const [activeTab, setActiveTab] = useState(0);
  const [categories, setCategories] = useState([]);
  const [entries, setEntries] = useState([]);
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState('');
  
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [categoryToEdit, setCategoryToEdit] = useState(null);

  const fetchCategoriesList = useCallback(async () => {
    const res = await getCategories(authToken);
    if (res.data || res.Categories) {
      setCategories(res.data || res.Categories || []);
    }
  }, [authToken]);

  const fetchEntriesList = useCallback(async () => {
    const res = await getEntries(authToken, selectedCategoryFilter);
    if (res.Entries || res.Munasibaat || res.data) {
      setEntries(res.Entries || res.Munasibaat || res.data || []);
    }
  }, [authToken, selectedCategoryFilter]);

  useEffect(() => {
    fetchCategoriesList();
    fetchEntriesList();
  }, [fetchCategoriesList, fetchEntriesList]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleOpenCreateCategory = () => {
    setCategoryToEdit(null);
    setCategoryModalOpen(true);
  };

  const handleOpenEditCategory = (category) => {
    setCategoryToEdit(category);
    setCategoryModalOpen(true);
  };

  const handleDeleteCategoryItem = async (id) => {
    if (window.confirm("Are you sure you want to delete this category and all its entries?")) {
      const res = await deleteCategory(authToken, id);
      if (res.success) {
        alert("Category deleted successfully");
        fetchCategoriesList();
        fetchEntriesList();
      } else {
        alert(res.errors ? res.errors.join(", ") : "Failed to delete category");
      }
    }
  };

  const handleDeleteEntryItem = async (id) => {
    if (window.confirm("Are you sure you want to delete this entry?")) {
      const res = await deleteEntry(authToken, id);
      if (res.success || res.Result) {
        alert("Entry deleted successfully");
        fetchEntriesList();
      } else {
        alert("Failed to delete entry");
      }
    }
  };

  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      <div className="glass-panel p-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className={`font-extrabold text-2xl sm:text-3xl ${isDark ? 'text-white' : 'text-slate-900'}`}>Munasibaat Management</h1>
          <p className={`text-xs mt-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Manage occasion categories, banners, and annual event entries</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            className="glass-btn-secondary px-4 py-2 text-xs font-semibold"
            onClick={handleOpenCreateCategory}
          >
            + Add Category
          </button>
          <button
            className="glass-btn-primary px-4 py-2 text-xs font-semibold"
            onClick={() => navigate('/munasibaat/createMunasibaat')}
          >
            + Add Yearly Entry
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <Box sx={{ borderBottom: 1, borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label={`Munasibaat Categories (${categories.length})`} />
          <Tab label={`Yearly Entries (${entries.length})`} />
        </Tabs>
      </Box>

      {/* TAB 0: CATEGORIES */}
      {activeTab === 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {categories.length > 0 ? (
            categories.map((cat) => (
              <div key={cat.id} className="glass-card p-5 flex flex-col justify-between">
                <div>
                  {cat.cover_image ? (
                    <img src={`${IMAGE_SHOW}/${cat.cover_image}`} alt={cat.title} className="w-full h-40 object-cover rounded-xl mb-3 border border-white/10" />
                  ) : (
                    <div className="w-full h-40 bg-slate-800/60 rounded-xl mb-3 flex items-center justify-center text-slate-500 font-semibold text-xs border border-white/10">
                      No Cover Image
                    </div>
                  )}
                  <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{cat.title}</h3>
                  <p className={`text-xs mt-1 line-clamp-2 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>{cat.description || 'No description provided.'}</p>
                </div>
                <div className="mt-4 pt-3 border-t border-white/10 flex justify-between items-center">
                  <span className="text-[11px] font-bold px-2.5 py-0.5 rounded-full bg-indigo-500/20 text-indigo-400 border border-indigo-500/30">
                    ID: #{cat.id}
                  </span>
                  <div className="flex space-x-2">
                    <button 
                      className="glass-btn-secondary px-3 py-1.5 text-xs font-medium"
                      onClick={() => handleOpenEditCategory(cat)}
                    >
                      Edit
                    </button>
                    <button 
                      className="glass-btn-danger px-3 py-1.5 text-xs font-medium"
                      onClick={() => handleDeleteCategoryItem(cat.id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="col-span-full glass-panel py-12 text-center text-slate-400 text-sm">
              No categories found. Click <strong>"+ Add Category"</strong> above to create one.
            </div>
          )}
        </div>
      )}

      {/* TAB 1: YEARLY ENTRIES */}
      {activeTab === 1 && (
        <div className="space-y-4">
          <div className="glass-panel p-4 flex items-center gap-3">
            <label className={`text-xs font-semibold ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>Filter by Category:</label>
            <select
              className="glass-input max-w-xs"
              value={selectedCategoryFilter}
              onChange={(e) => setSelectedCategoryFilter(e.target.value)}
            >
              <option value="">All Categories</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>{c.title}</option>
              ))}
            </select>
          </div>

          <div className="glass-panel overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full min-w-full glass-table">
                <thead>
                  <tr>
                    <th className="py-3.5 px-4 text-center font-semibold text-xs uppercase tracking-wider">Cover Image</th>
                    <th className="py-3.5 px-4 text-left font-semibold text-xs uppercase tracking-wider">Category</th>
                    <th className="py-3.5 px-4 text-left font-semibold text-xs uppercase tracking-wider">Year</th>
                    <th className="py-3.5 px-4 text-left font-semibold text-xs uppercase tracking-wider">Title</th>
                    <th className="py-3.5 px-4 text-center font-semibold text-xs uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.length > 0 ? (
                    entries.map((item) => {
                      const firstImg = Array.isArray(item.images) && item.images.length > 0 ? item.images[0] : item.banner;
                      return (
                        <tr key={item.id} className="transition-colors hover:bg-white/5">
                          <td className="py-3 px-4 text-center">
                            <div className="flex justify-center items-center">
                              {firstImg ? (
                                <img src={`${IMAGE_SHOW}/${firstImg}`} alt="Preview" className="w-20 h-14 object-cover rounded-lg border border-white/10" />
                              ) : (
                                <span className="text-xs text-slate-500">No Image</span>
                              )}
                            </div>
                          </td>
                          <td className="py-3 px-4 text-xs font-bold text-cyan-400">
                            {item.category_name || item.category || 'N/A'}
                          </td>
                          <td className="py-3 px-4 text-xs font-mono font-semibold">
                            {item.year}
                          </td>
                          <td className="py-3 px-4 text-xs font-semibold">
                            {item.title || 'Untitled Entry'}
                          </td>
                          <td className="py-3 px-4 text-center">
                            <div className="flex items-center justify-center space-x-2">
                              <button
                                className="glass-btn-secondary px-3 py-1.5 text-xs font-medium"
                                onClick={() => navigate('/munasibaat/editMunasibaat', { state: { munasibaatId: item.id } })}
                              >
                                Edit / View
                              </button>
                              <button
                                className="glass-btn-danger px-3 py-1.5 text-xs font-medium"
                                onClick={() => handleDeleteEntryItem(item.id)}
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="5" className="py-8 text-center text-sm text-slate-400">
                        No entries found. Click <strong>"+ Add Yearly Entry"</strong> to add an entry.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <CreateCategoryModal
        open={categoryModalOpen}
        onClose={() => setCategoryModalOpen(false)}
        categoryToEdit={categoryToEdit}
        onSuccess={() => {
          fetchCategoriesList();
          fetchEntriesList();
        }}
      />
    </div>
  );
};

export default Munasibaat;